import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";

import { validateAddress } from "utils/addressHelpers";
import apis from "context/api";

function AddressModal({ open, onClose, onSave, customerId, address }) {
  const [formData, setFormData] = useState({
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "India",
    type: "shipping",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    if (open && address) {
      setFormData({
        addressLine1: address.addressLine1 || "",
        addressLine2: address.addressLine2 || "",
        city: address.city || "",
        state: address.state || "",
        postalCode: address.postalCode || "",
        country: address.country || "",
        type: address.type || "shipping",
      });
    } else if (open && !address) {
      setFormData({
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        postalCode: "",
        country: "India",
        type: "shipping",
      });
    }
    setErrors({});
    setApiError(null);
  }, [open, address]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSave = () => {
    setApiError(null);

    // Validate address
    const validation = validateAddress(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    // Validate customerId
    if (!customerId) {
      setApiError("Customer ID is required. Please save customer first.");
      return;
    }

    setLoading(true);

    // Retry logic for handling "Customer not found" errors due to timing issues
    const attemptSave = (retryCount = 0, maxRetries = 3) => {
      const savePromise =
        address && address.id
          ? apis.updateAddress(customerId, address.id, formData)
          : apis.createAddress(customerId, formData);

      savePromise
        .then((result) => {
          setLoading(false);
          onSave(result);
          onClose();
        })
        .catch((err) => {
          // If "Customer not found" error and we have retries left, try again with delay
          if (
            err.message &&
            err.message.includes("Customer not found") &&
            retryCount < maxRetries
          ) {
            setTimeout(() => {
              attemptSave(retryCount + 1, maxRetries);
            }, 1000 * (retryCount + 1)); // Delay: 1s, 2s, 3s
          } else {
            setLoading(false);
            setApiError(err.message || "Failed to save address");
          }
        });
    };

    attemptSave();
  };

  const handleCancel = () => {
    setErrors({});
    setApiError(null);
    onClose();
  };

  const isEdit = address && address.id;

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: "1.25rem", fontWeight: 600 }}>
        {isEdit ? "Edit Address" : "Add Address"}
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {apiError && (
          <MDBox mb={2} p={1.5} bgColor="error" borderRadius="lg">
            <MDTypography variant="caption" color="white">
              {apiError}
            </MDTypography>
          </MDBox>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput
              fullWidth
              label="Address Line 1"
              name="addressLine1"
              variant="outlined"
              value={formData.addressLine1}
              onChange={handleChange}
              error={!!errors.addressLine1}
              helperText={errors.addressLine1}
              FormHelperTextProps={{
                sx: { color: "#f44335" },
              }}
            />
          </Grid>

          <Grid item xs={12} style={{ display: "none" }}>
            <MDInput
              fullWidth
              label="Address Line 2 (optional)"
              name="addressLine2"
              variant="outlined"
              value={formData.addressLine2}
              onChange={handleChange}
              error={!!errors.addressLine2}
              helperText={errors.addressLine2}
              FormHelperTextProps={{
                sx: { color: "#f44335" },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <MDInput
              fullWidth
              label="City"
              name="city"
              variant="outlined"
              value={formData.city}
              onChange={handleChange}
              error={!!errors.city}
              helperText={errors.city}
              FormHelperTextProps={{
                sx: { color: "#f44335" },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <MDInput
              fullWidth
              label="State"
              name="state"
              variant="outlined"
              value={formData.state}
              onChange={handleChange}
              error={!!errors.state}
              helperText={errors.state}
              FormHelperTextProps={{
                sx: { color: "#f44335" },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <MDInput
              fullWidth
              label="Postal Code"
              name="postalCode"
              variant="outlined"
              value={formData.postalCode}
              onChange={handleChange}
              error={!!errors.postalCode}
              helperText={errors.postalCode}
              FormHelperTextProps={{
                sx: { color: "#f44335" },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <MDInput
              fullWidth
              label="Country"
              name="country"
              variant="outlined"
              value={formData.country}
              onChange={handleChange}
              error={!!errors.country}
              helperText={errors.country}
              FormHelperTextProps={{
                sx: { color: "#f44335" },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.type} style={{ height: "45px" }}>
              <InputLabel id="address-type-label" style={{ height: "45px" }}>
                Type
              </InputLabel>
              <Select
                labelId="address-type-label"
                id="address-type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                label="Type"
                style={{ height: "45px" }}
              >
                <MenuItem value="shipping">Shipping</MenuItem>
                <MenuItem value="billing">Billing</MenuItem>
              </Select>
              {errors.type && (
                <MDTypography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                  {errors.type}
                </MDTypography>
              )}
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <MDButton variant="gradient" color="warning" onClick={handleCancel} disabled={loading}>
          Cancel
        </MDButton>
        <MDButton variant="gradient" color="info" onClick={handleSave} disabled={loading}>
          {loading ? "Saving..." : "Save"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

AddressModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  customerId: PropTypes.string.isRequired,
  address: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    addressLine1: PropTypes.string,
    addressLine2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postalCode: PropTypes.string,
    country: PropTypes.string,
    type: PropTypes.string,
  }),
};

AddressModal.defaultProps = {
  address: null,
};

export default AddressModal;
